import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getPlans,
  createCheckoutSession,
  validateBypassCode,
} from '@/services/billing-service/billing.service';
import { Plan, PlanFeatures } from '@/services/billing-service/billing.service.types';
import { Button } from '@/components/button/button';
import { Flex, Spin, Typography, Input, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { openNotification } from '@/utils/notification/notification';
import { useState } from 'react';
import clsx from 'clsx';
import i18n, { type ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@/assets/icons/check-circle.svg?react';
import MinusCircleIcon from '@/assets/icons/minus-circle.svg?react';
import styles from './select-plan.module.scss';

const { Text } = Typography;

// `labelKey` points into the `onboarding` catalogue; `t()` runs while rendering.
const FEATURE_LABELS: {
  key: keyof Omit<PlanFeatures, 'audio_retention_days'>;
  labelKey: ParseKeys<'onboarding'>;
}[] = [
  { key: 'topics', labelKey: 'selectPlan.features.topics' },
  { key: 'sentiment', labelKey: 'selectPlan.features.sentiment' },
  { key: 'translation', labelKey: 'selectPlan.features.translation' },
  { key: 'advanced_analytics', labelKey: 'selectPlan.features.advancedAnalytics' },
];

// Keyed by the plan name the API sends, which is not translated.
const PLAN_TAGLINE_KEYS: Partial<Record<string, ParseKeys<'onboarding'>>> = {
  Starter: 'selectPlan.taglines.starter',
  Professional: 'selectPlan.taglines.professional',
  Enterprise: 'selectPlan.taglines.enterprise',
};

// The interval is an API value; only the label is ours. Anything unknown is shown as sent.
const INTERVAL_LABEL_KEYS: Partial<Record<string, ParseKeys<'onboarding'>>> = {
  month: 'selectPlan.interval.month',
  year: 'selectPlan.interval.year',
};

const RECOMMENDED_PLAN = 'Professional';

function parsePriceParts(amount: number, currency: string) {
  // `narrowSymbol` keeps "$" instead of the "USD" that the pl locale would otherwise print.
  const formatter = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
  });
  const parts = formatter.formatToParts(amount / 100);
  const symbol = parts.find((p) => p.type === 'currency')?.value || '$';
  const value = parts
    .filter((p) => p.type === 'integer' || p.type === 'group' || p.type === 'decimal' || p.type === 'fraction')
    .map((p) => p.value)
    .join('');
  return { symbol, value };
}

function PlanCard({
  plan,
  isSelected,
  onSelect,
}: {
  plan: Plan;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('onboarding');
  const monthlyPrice = plan.prices.find((p) => p.interval === 'month');
  const yearlyPrice = plan.prices.find((p) => p.interval === 'year');
  const displayPrice = monthlyPrice || yearlyPrice || plan.prices[0];
  const intervalKey = displayPrice ? INTERVAL_LABEL_KEYS[displayPrice.interval] : undefined;
  const taglineKey = PLAN_TAGLINE_KEYS[plan.name];
  const isRecommended = plan.name === RECOMMENDED_PLAN;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  const priceParts = displayPrice ? parsePriceParts(displayPrice.amount, displayPrice.currency) : null;

  return (
    <div
      className={clsx(
        styles.planCard,
        isSelected && styles.planCardSelected,
        isRecommended && !isSelected && styles.planCardRecommended,
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      {isRecommended && (
        <div className={styles.recommendedBadge}>{t('selectPlan.mostPopular')}</div>
      )}

      <Text className={styles.planName}>{plan.name}</Text>

      {taglineKey && <Text className={styles.planTagline}>{t(taglineKey)}</Text>}

      {priceParts && displayPrice && (
        <Text className={styles.priceBlock}>
          <span className={styles.currencySymbol}>{priceParts.symbol}</span>
          <span className={styles.priceAmount}>{priceParts.value}</span>
          <span className={styles.priceInterval}>
            /{intervalKey ? t(intervalKey) : displayPrice.interval}
          </span>
        </Text>
      )}

      <div className={styles.divider} />

      <ul className={styles.features}>
        {FEATURE_LABELS.map(({ key, labelKey }) => {
          const included = plan.features[key];
          return (
            <li key={key} className={styles.featureRow}>
              <span
                className={clsx(
                  styles.featureIcon,
                  included ? styles.featureIconIncluded : styles.featureIconExcluded,
                )}
              >
                {included ? (
                  <CheckCircleIcon width={20} height={20} stroke={styles.checkColor} />
                ) : (
                  <MinusCircleIcon width={20} height={20} stroke={styles.minusColor} />
                )}
              </span>
              <span className={clsx(styles.featureLabel, !included && styles.featureLabelDisabled)}>
                {t(labelKey)}
              </span>
            </li>
          );
        })}
        {plan.features.audio_retention_days != null && (
          <li className={styles.featureRow}>
            <span className={clsx(styles.featureIcon, styles.featureIconIncluded)}>
              <CheckCircleIcon width={20} height={20} stroke={styles.checkColor} />
            </span>
            <span className={styles.featureLabel}>
              {t('selectPlan.audioRetention', { count: plan.features.audio_retention_days })}
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

export function SelectPlan() {
  const { t } = useTranslation(['onboarding', 'common']);
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [bypassCode, setBypassCode] = useState('');
  const [validatedCode, setValidatedCode] = useState<string | null>(null);
  const [discount, setDiscount] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: getPlans,
  });

  const { mutate: validate, isPending: isValidating } = useMutation({
    mutationFn: validateBypassCode,
    onSuccess: (response) => {
      if (response.valid) {
        setValidatedCode(bypassCode);
        setDiscount(response.discount || null);
        setCodeError(null);
      } else {
        setCodeError(response.error || t('onboarding:selectPlan.promo.invalid'));
        setValidatedCode(null);
        setDiscount(null);
      }
    },
    onError: () => {
      setCodeError(t('onboarding:selectPlan.promo.validationFailed'));
      setValidatedCode(null);
      setDiscount(null);
    },
  });

  const { mutate: checkout, isPending: isCheckingOut } = useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (response) => {
      navigate('/onboarding/checkout', {
        state: { clientSecret: response.client_secret },
      });
    },
    onError: () => {
      openNotification({
        message: t('onboarding:selectPlan.checkoutFailed'),
        type: 'error',
      });
    },
  });

  const handleContinue = () => {
    if (!selectedPlan) return;
    const monthlyPrice = selectedPlan.prices.find((p) => p.interval === 'month');
    const yearlyPrice = selectedPlan.prices.find((p) => p.interval === 'year');
    const displayPrice = monthlyPrice || yearlyPrice || selectedPlan.prices[0];
    if (!displayPrice) return;

    checkout({
      price_id: displayPrice.price_id,
      success_url: `${window.location.origin}/onboarding/success`,
      ...(validatedCode ? { bypass_code: validatedCode } : {}),
    });
  };

  const handleApplyCode = () => {
    const trimmed = bypassCode.trim();
    if (!trimmed) return;
    validate(trimmed);
  };

  const handleRemoveCode = () => {
    setValidatedCode(null);
    setDiscount(null);
    setBypassCode('');
    setCodeError(null);
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <h2 className={styles.heading}>{t('onboarding:selectPlan.title')}</h2>
        <Text className={styles.subtitle}>{t('onboarding:selectPlan.subtitle')}</Text>
      </div>

      <div className={styles.cardsGrid}>
        {data?.plans.map((plan) => (
          <PlanCard
            key={plan.name}
            plan={plan}
            isSelected={selectedPlan?.name === plan.name}
            onSelect={() => setSelectedPlan(plan)}
          />
        ))}
      </div>

      <div className={styles.promoSection}>
        <Text className={styles.promoLabel}>{t('onboarding:selectPlan.promo.label')}</Text>
        {validatedCode ? (
          <Flex align="center" gap={8}>
            <Tag color="green" closable onClose={handleRemoveCode}>
              {validatedCode} — {discount}
            </Tag>
          </Flex>
        ) : (
          <Flex gap={8}>
            <Input
              placeholder={t('onboarding:selectPlan.promo.placeholder')}
              value={bypassCode}
              onChange={(e) => {
                setBypassCode(e.target.value);
                setCodeError(null);
              }}
              onPressEnter={handleApplyCode}
              status={codeError ? 'error' : undefined}
              className={styles.promoInput}
              data-testid="bypass-code-input"
            />
            <Button
              buttonVariant="secondary"
              buttonSize="small"
              onClick={handleApplyCode}
              loading={isValidating}
              disabled={!bypassCode.trim()}
              data-testid="apply-code-btn"
            >
              {t('common:actions.apply')}
            </Button>
          </Flex>
        )}
        {codeError && <Text className={styles.promoError}>{codeError}</Text>}
      </div>

      <Button
        type="primary"
        size="large"
        loading={isCheckingOut}
        disabled={!selectedPlan}
        onClick={handleContinue}
        className={styles.continueBtn}
      >
        {t('onboarding:selectPlan.continueWith', { name: selectedPlan?.name || '…' })}
      </Button>
    </div>
  );
}